import { memo, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Tab } from 'pos-client/interfaces/tab.ts';
import type { PosPageHeaderProps } from 'pos-client/layouts/page/PosPageHeader.tsx';

import { PosTabs, type PosTabsProps } from './PosTabs.tsx';

type TabProps = Pick<
  PosTabsProps,
  | 'disableStickyTabs'
  | 'hideToolbar'
  | 'renderContentAfterLoad'
  | 'tabBarExtraContent'
  | 'stickyToolbar'
  | 'destroyOnHidden'
  | 'defaultActiveKey'
>;

interface TabsOutletProps extends Omit<PosPageHeaderProps, 'tabBar'>, TabProps {
  tabs: Tab[];
}

export const TabsOutlet = memo(
  ({
    tabs,
    defaultActiveKey,
    tabBarExtraContent,
    disableStickyTabs = false,
    stickyToolbar = false,
    loading,
    renderContentAfterLoad = true,
    hideToolbar = false,
    destroyOnHidden = false,
    ...headerProps
  }: TabsOutletProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const activeKey = useMemo(() => {
      const keys = tabs.map((t) => t.key);
      return location.pathname
        .split('/')
        .reverse()
        .find((k) => keys.includes(k));
    }, [location.pathname, tabs]);

    const onChangeTab = (tab: string) => navigate(tab);

    return (
      <PosTabs
        onChange={onChangeTab}
        defaultActiveKey={defaultActiveKey}
        activeKey={activeKey}
        items={tabs}
        tabBarExtraContent={tabBarExtraContent}
        disableStickyTabs={disableStickyTabs}
        headerProps={headerProps}
        stickyToolbar={stickyToolbar}
        loading={loading}
        renderContentAfterLoad={renderContentAfterLoad}
        hideToolbar={hideToolbar}
        destroyOnHidden={destroyOnHidden}
        pageTabs
      />
    );
  }
);
